/**
 * useDashboards - Hook para gerenciar dashboards do CRM
 * 
 * Busca dashboards disponíveis para o usuário baseado em suas permissões.
 * Suporta seleção persistente via localStorage.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger para este módulo
const log = createLogger('useDashboards');

// Interface de Dashboard retornado pela RPC
export interface Dashboard {
  id: string;
  name: string;
  description: string | null;
  slug: string;
  color: string;
  icon: string;
  display_order: number;
  component_key: string;
  is_default: boolean;
  module: string;
}

// Chave do localStorage para persistir seleção
const STORAGE_KEY = 'crm_selected_dashboard';

/**
 * Hook principal para gerenciamento de dashboards
 */
export function useDashboards() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Estado da seleção do dashboard
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(() => {
    // Recupera seleção do localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY);
    }
    return null;
  });

  // Query para buscar dashboards do usuário
  const {
    data: dashboards = [],
    isLoading,
    error,
    refetch,
  } = useQuery<Dashboard[]>({
    queryKey: ['user-dashboards', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) {
        log.debug('Tenant não definido, retornando array vazio');
        return [];
      }

      log.debug('Buscando dashboards para tenant', currentTenant.id);

      const { data, error } = await supabase.rpc('get_user_dashboards', {
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        log.error('Erro ao buscar dashboards', error.message);
        throw error;
      }

      log.debug('Dashboards encontrados', String(data?.length || 0));
      return (data || []) as Dashboard[];
    },
    enabled: !!currentTenant?.id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Seleciona dashboard padrão se nenhum estiver selecionado
  useEffect(() => {
    if (dashboards.length > 0 && !selectedDashboardId) {
      // Primeiro tenta encontrar o dashboard padrão
      const defaultDashboard = dashboards.find(d => d.is_default);
      const dashboardToSelect = defaultDashboard || dashboards[0];
      
      log.debug('Selecionando dashboard padrão', dashboardToSelect.name);
      setSelectedDashboardId(dashboardToSelect.id);
      localStorage.setItem(STORAGE_KEY, dashboardToSelect.id);
    }
  }, [dashboards, selectedDashboardId]);

  // Valida se o dashboard selecionado existe na lista
  useEffect(() => {
    if (selectedDashboardId && dashboards.length > 0) {
      const exists = dashboards.some(d => d.id === selectedDashboardId);
      if (!exists) {
        log.warn('Dashboard selecionado não encontrado, resetando');
        const defaultDashboard = dashboards.find(d => d.is_default) || dashboards[0];
        setSelectedDashboardId(defaultDashboard.id);
        localStorage.setItem(STORAGE_KEY, defaultDashboard.id);
      }
    }
  }, [dashboards, selectedDashboardId]);

  // Função para selecionar dashboard
  const selectDashboard = useCallback((dashboardId: string) => {
    log.debug('Selecionando dashboard', dashboardId);
    setSelectedDashboardId(dashboardId);
    localStorage.setItem(STORAGE_KEY, dashboardId);
  }, []);

  // Dashboard atualmente selecionado
  const selectedDashboard = dashboards.find(d => d.id === selectedDashboardId) || null;

  return {
    // Lista de dashboards disponíveis
    dashboards,
    // Dashboard selecionado atualmente
    selectedDashboard,
    // ID do dashboard selecionado
    selectedDashboardId,
    // Função para selecionar dashboard
    selectDashboard,
    // Estados de loading e erro
    isLoading,
    error,
    // Função para recarregar dashboards
    refetch,
  };
}

/**
 * Hook para buscar dashboards filtrados por módulo (conversations, crm, financial)
 * Reutiliza o hook principal e filtra client-side por module
 */
export function useModuleDashboards(module: string) {
  const result = useDashboards();
  
  // Filtra dashboards pelo módulo
  const moduleDashboards = result.dashboards.filter(d => d.module === module);
  
  // Seleciona o dashboard padrão do módulo se nenhum selecionado ou o selecionado não pertence ao módulo
  const selectedInModule = moduleDashboards.find(d => d.id === result.selectedDashboardId);
  const effectiveSelected = selectedInModule || moduleDashboards.find(d => d.is_default) || moduleDashboards[0] || null;

  return {
    ...result,
    dashboards: moduleDashboards,
    selectedDashboard: effectiveSelected,
    selectedDashboardId: effectiveSelected?.id || null,
  };
}

/**
 * Hook para admin gerenciar permissões de dashboards
 */
export function useDashboardsAdmin() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Logger para admin
  const adminLog = createLogger('useDashboardsAdmin');

  // Query para buscar todos os dashboards (admin only)
  const {
    data: dashboards = [],
    isLoading,
    error,
    refetch,
  } = useQuery<(Dashboard & { is_active: boolean })[]>({
    queryKey: ['admin-dashboards', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const { data, error } = await supabase.rpc('get_all_dashboards_for_admin', {
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;
      return (data || []) as (Dashboard & { is_active: boolean })[];
    },
    enabled: !!currentTenant?.id,
  });

  // Busca permissões de um dashboard específico
  const fetchDashboardPermissions = useCallback(async (dashboardId: string) => {
    if (!currentTenant?.id) return null;

    const { data, error } = await supabase.rpc('get_dashboard_permissions', {
      p_dashboard_id: dashboardId,
      p_tenant_hint: currentTenant.id,
    });

    if (error) {
      adminLog.error('Erro ao buscar permissões', error.message);
      throw error;
    }

    return data as {
      dashboard_id: string;
      profile_ids: string[];
      user_ids: string[];
    };
  }, [currentTenant?.id]);

  // Atualiza permissões por perfil
  const updateProfileAccess = useCallback(async (
    dashboardId: string,
    profileIds: string[]
  ) => {
    if (!currentTenant?.id) return;

    const { data, error } = await supabase.rpc('update_dashboard_profile_access', {
      p_dashboard_id: dashboardId,
      p_profile_ids: profileIds,
      p_tenant_hint: currentTenant.id,
    });

    if (error) {
      adminLog.error('Erro ao atualizar permissões por perfil', error.message);
      throw error;
    }

    // Invalida cache para atualizar a lista
    queryClient.invalidateQueries({ queryKey: ['user-dashboards'] });
    return data;
  }, [currentTenant?.id, queryClient]);

  // Atualiza permissões por usuário
  const updateUserAccess = useCallback(async (
    dashboardId: string,
    userIds: string[]
  ) => {
    if (!currentTenant?.id) return;

    const { data, error } = await supabase.rpc('update_dashboard_user_access', {
      p_dashboard_id: dashboardId,
      p_user_ids: userIds,
      p_tenant_hint: currentTenant.id,
    });

    if (error) {
      adminLog.error('Erro ao atualizar permissões por usuário', error.message);
      throw error;
    }

    // Invalida cache para atualizar a lista
    queryClient.invalidateQueries({ queryKey: ['user-dashboards'] });
    return data;
  }, [currentTenant?.id, queryClient]);

  return {
    dashboards,
    isLoading,
    error,
    refetch,
    fetchDashboardPermissions,
    updateProfileAccess,
    updateUserAccess,
  };
}
